import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Calendar, Award } from 'lucide-react';

const Chart = ({title}) => {
  const [timeRange, setTimeRange] = useState('month');

  // Sample data for different charts
  const weeklyData = [
    { day: 'Mon', breakfast: 320, lunch: 450, dinner: 380 },
    { day: 'Tue', breakfast: 340, lunch: 470, dinner: 390 },
    { day: 'Wed', breakfast: 310, lunch: 440, dinner: 370 },
    { day: 'Thu', breakfast: 350, lunch: 480, dinner: 400 },
    { day: 'Fri', breakfast: 330, lunch: 460, dinner: 385 },
    { day: 'Sat', breakfast: 380, lunch: 520, dinner: 450 },
    { day: 'Sun', breakfast: 360, lunch: 500, dinner: 420 }
  ];

  const monthlyData = [
    { month: 'Jan', total: 12500 },
    { month: 'Feb', total: 13200 },
    { month: 'Mar', total: 14100 },
    { month: 'Apr', total: 13800 },
    { month: 'May', total: 15300 },
    { month: 'Jun', total: 14900 }
  ];

  const mealTypeData = [
    { name: 'Breakfast', value: 2340, color: '#FF6B6B' },
    { name: 'Lunch', value: 3290, color: '#0f2c7a' },
    { name: 'Dinner', value: 2795, color: '#4b50da' }
  ];

  const stats = [
    { title: 'Total Meals Today', value: '1,234', icon: Users, color: 'bg-blue-500', change: '+12%' },
    { title: 'This Week', value: '8,425', icon: Calendar, color: 'bg-green-500', change: '+8%' },
    { title: 'This Month', value: '35,670', icon: TrendingUp, color: 'bg-purple-500', change: '+15%' },
    { title: 'Avg per Day', value: '1,189', icon: Award, color: 'bg-orange-500', change: '+5%' }
  ];

  const COLORS = ['#FF6B6B', '#6651db', '#5445ca', '#FFA07A'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">{title}</h1>
          <p className="text-slate-600">Comprehensive overview of meal distribution and statistics</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-green-600 text-sm font-semibold">{stat.change}</span>
                </div>
                <h3 className="text-slate-600 text-sm mb-1">{stat.title}</h3>
                <p className="text-3xl font-bold text-slate-800">{stat.value}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Weekly Breakdown Bar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">Weekly Breakdown</h2>
              <select 
                className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }} 
                />
                <Legend />
                <Bar dataKey="breakfast" fill="#FF6B6B" radius={[8, 8, 0, 0]} />
                <Bar dataKey="lunch" fill="#4ECDC4" radius={[8, 8, 0, 0]} />
                <Bar dataKey="dinner" fill="#403cc9" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Meal Type Distribution Pie Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-6">Meal Type Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mealTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {mealTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-6 space-y-3">
              {mealTypeData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded-full mr-3" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-slate-700">{item.name}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Monthly Trend Line Chart */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Monthly Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#8b5cf6" 
                strokeWidth={3}
                dot={{ fill: '#8b5cf6', r: 6 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Chart;